import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/** Live site the pages are canonical to (URLs match ghru.edu.in one-to-one). */
export const SITE_URL = 'https://ghru.edu.in';

export interface PageSeo {
  title: string;
  description: string;
  /** Absolute URL; defaults to SITE_URL + the current path */
  canonical?: string;
}

/**
 * One place that sets the page <title>, meta description and canonical link.
 * AppComponent calls it from route data on every navigation; pages whose SEO
 * comes from their content (course, blog, event) call it themselves.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  update({ title, description, canonical }: PageSeo): void {
    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });

    const url = canonical ?? this.urlFor(this.doc.location.pathname);
    this.meta.updateTag({ property: 'og:url', content: url });
    this.canonicalLink().href = url;
  }

  /** Canonical URL for a path: no query string or hash, no trailing slash */
  urlFor(path: string): string {
    const clean = path.split(/[?#]/)[0].replace(/\/+$/, '');
    return SITE_URL + (clean || '/');
  }

  private canonicalLink(): HTMLLinkElement {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.rel = 'canonical';
      this.doc.head.appendChild(link);
    }
    return link;
  }
}
